import React, { useEffect, useRef } from "react";
import { App, type URLOpenListenerEvent } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { PayrexxCheckoutService } from "../services/PayrexxCheckoutService";
import { TicketPurchaseApi } from "../services/TicketPurchaseApi";
import { ephemeralStore } from "../store/ephemeral";
import { ticketPurchaseSlice } from "../store/ephemeral/slices/ticketPurchase.slice";
import {
    isPayrexxNativeReturnUrl,
    parsePayrexxReturnParams,
    resumePayrexxReturn,
    verifyPaymentUntilSettled,
} from "./TicketPurchase/payrexxReturn.tools";

/**
 * Grace period during which a `browserFinished` event is attributed to a deep
 * link return we already handled (closing the in-app browser fires both).
 */
const BROWSER_FINISHED_DEEPLINK_GRACE_MS = 10_000;

/**
 * Side-effect component handling the Payrexx return inside the native shell.
 *
 * - `appUrlOpen` on `flowride://payment/payrexx/return`: closes the in-app
 *   browser, verifies the payment through the backend and reopens the wizard.
 * - `browserFinished` (user dismissed the in-app browser without a deep link):
 *   re-checks the payment; a still-`pending` payment is cancelled and its
 *   gateway deleted, a confirmed one resumes the wizard on the booking step.
 *
 * Mounted once in `App`; renders `null`.
 */
export const PayrexxNativeReturnBootstrap: React.FC = () => {
    /** Timestamp of the last handled deep link, for the `browserFinished` grace check. */
    const lastDeepLinkHandledAtRef = useRef( 0 );

    useEffect( () => {
        if ( !PayrexxCheckoutService.isNativeCheckout() ) {
            return undefined;
        }

        const urlListener = App.addListener( "appUrlOpen", ( event: URLOpenListenerEvent ) => {
            if ( !isPayrexxNativeReturnUrl( event.url ) ) {
                return;
            }

            lastDeepLinkHandledAtRef.current = Date.now();

            void ( async() => {
                // Closing the browser first: the shopper is back in the app already.
                await PayrexxCheckoutService.closeCheckout();

                const params = parsePayrexxReturnParams( event.url );

                if ( params.attemptId === null ) {
                    return;
                }

                const verifiedStatus = await verifyPaymentUntilSettled( params.attemptId ?? "", params.paymentId ?? undefined );

                await resumePayrexxReturn( params.attemptId ?? "", verifiedStatus, params.paymentId );
            } )();
        } );

        const browserFinishedListener = Browser.addListener( "browserFinished", () => {
            // The deep link already handled this close — nothing more to do.
            if ( Date.now() - lastDeepLinkHandledAtRef.current < BROWSER_FINISHED_DEEPLINK_GRACE_MS ) {
                return;
            }

            const state = ephemeralStore.peek( ticketPurchaseSlice );
            const attemptId = state.purchaseAttemptId ?? state.prebookingResponse?.purchaseAttemptId ?? null;

            if ( attemptId === null || state.paymentStatus !== "awaiting_return" ) {
                return;
            }

            void ( async() => {
                /*
                 * The shopper closed the checkout manually: re-check before deciding —
                 * the payment may have been confirmed just before the close.
                 */
                const verifiedStatus = await verifyPaymentUntilSettled( attemptId, state.paymentId ?? undefined );

                if ( verifiedStatus === null || verifiedStatus === "pending" ) {
                    // No money movement detected: cancel the pending payment (deletes the gateway).
                    try {
                        await TicketPurchaseApi.postPurchasePaymentCancel( { "purchaseAttemptId": attemptId } );
                    } catch {
                        // Best effort — the gateway validity bounds its lifetime regardless.
                    }
                }

                await resumePayrexxReturn( attemptId, verifiedStatus, state.paymentId );
            } )();
        } );

        return () => {
            void urlListener.then( ( handle ) => handle.remove() );
            void browserFinishedListener.then( ( handle ) => handle.remove() );
        };
    }, [] );

    return null;
};
